import { Injectable } from '@angular/core';

import { MingaPermission } from 'libs/domain';
import { mingaSettingTypes } from 'libs/util';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { PermissionsService } from '@app/src/app/permissions';
import { UserStorage } from '@app/src/app/services/UserStorage';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { RedeemPointsModalService } from '@shared/components/redeem-points-modal';
import { getAppColor } from '@shared/constants';
import { FlexTimePermissionsService } from '@shared/services/flex-time';
import { KioskPermissionsService } from '@shared/services/kiosk/kiosk-permissions.service';

import { ACTION_KEY_DEFAULTS, ActionMesssages } from '../constants/tools.constants';
import { ACTION_CATEGORY, ACTION_KEY, ToolAction } from '../shared-tools/types/shared-tools.types';
import { StudentToolsCheckinService } from '../student-tools/services';
import { PBIS_CHECKIN_TYPES } from '../teacher-tools/constants/tt-view.constants';
import { TeacherToolsService } from '../teacher-tools/services';

@Injectable()
export class ToolsActionsService {
  // Constants

  private readonly FAVORITES_KEY = 'tools-favorite-actions';
  /**
   * Centralized list of all tools actions shared amons student and admin tools
   * (teacher tools will also use this once we migate to new design)
   */
  public readonly actions: ToolAction[] = [
    {
      key: ACTION_KEY.CREATE_HALL_PASS,
      id: 'tools-dashboard-click-assign-hallpass',
      label: ActionMesssages.HALL_PASS_BTN,
      icon: 'mg-hallpass-menu-o',
      iconColor: getAppColor('hall-pass'),
      onPress: () => {
        this._teacherToolsService.openHallpassAssignmentForm();
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isHallPassModuleEnabled(),
        this._permissionService.observePermission(MingaPermission.HALL_PASS_CREATE),
      ]).pipe(map(([settingEnabled, canCreate]) => settingEnabled && canCreate)),
      category: ACTION_CATEGORY.HALL_PASS,
      usedIn: ['student', 'teacher', 'admin'],
    },
    {
      key: ACTION_KEY.SELF_CHECK_IN,
      id: 'tools-dashboard-click-assign-checkin',
      label: ActionMesssages.SELF_CHECK_IN_BTN,
      icon: 'mg-checkin-menu-o',
      iconColor: getAppColor('check-in'),
      onPress: () => {
        this._studentToolsCheckinService.openScanner(false, true);
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isCheckinModuleEnabled(),
        this._mingaSettings.isFlexTimeModuleEnabled(),
      ]).pipe(
        map(([checkinModuleEnabled, flexTimeModuleEnabled]) => {
          return checkinModuleEnabled || flexTimeModuleEnabled;
        }),
      ),
      category: ACTION_CATEGORY.CHECK_IN,
      usedIn: ['student'],
    },
    {
      key: ACTION_KEY.CHECK_IN,
      id: 'tools-dashboard-click-self-checkin',
      label: ActionMesssages.CHECK_IN_BTN,
      icon: 'mg-checkin-menu-o',
      iconColor: getAppColor('check-in'),
      onPress: () => {
        this._teacherToolsService.openCheckinForm();
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isCheckinModuleEnabled(),
        this._permissionService.observePermission(MingaPermission.CHECKIN_MANAGE),
      ]).pipe(map(([settingEnabled, manageCheckin]) => settingEnabled && manageCheckin)),
      category: ACTION_CATEGORY.CHECK_IN,
      usedIn: ['teacher', 'admin'],
    },
    {
      key: ACTION_KEY.ASSIGN_ACTIVITY,
      id: 'tools-dashboard-click-assign-activity',
      label: ActionMesssages.ACTIVITY_BTN,
      icon: 'mg-flextime-menu-o',
      iconColor: getAppColor('flextime'),
      onPress: () => {
        this._teacherToolsService.openAssignActivityForm();
      },
      hasPermission$: this._mingaSettings
        .isFlexTimeModuleEnabled()
        .pipe(map(settingEnabled => settingEnabled && this._flexPermissionService.isFlexTimeSelfManagerOrHigher())),
      category: ACTION_CATEGORY.ACTIVITY,
      usedIn: ['teacher', 'admin'],
    },
    {
      key: ACTION_KEY.ASSIGN_PRAISE,
      id: 'tools-dashboard-click-assign-praise',
      label: ActionMesssages.PRAISE_BTN,
      icon: 'mg-praise-o',
      iconColor: getAppColor('behavior-praise'),
      onPress: () => {
        this._teacherToolsService.openPbisTrackingForm(PBIS_CHECKIN_TYPES.PRAISE);
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isPbisModuleEnabled(),
        this._permissionService.observePermission(MingaPermission.PBIS_CREATE),
      ]).pipe(map(([settingEnabled, canCreate]) => settingEnabled && canCreate)),
      category: ACTION_CATEGORY.PBIS,
      usedIn: ['student', 'teacher', 'admin'],
    },
    {
      key: ACTION_KEY.ASSIGN_GUIDANCE,
      id: 'tools-dashboard-click-assign-guidance',
      label: ActionMesssages.GUIDANCE_BTN,
      icon: 'mg-guidance-o',
      iconColor: getAppColor('behavior-consequence'),
      onPress: () => {
        this._teacherToolsService.openPbisTrackingForm(PBIS_CHECKIN_TYPES.GUIDANCE);
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isPbisModuleEnabled(),
        this._permissionService.observePermission(MingaPermission.PBIS_CREATE),
      ]).pipe(map(([settingEnabled, canCreate]) => settingEnabled && canCreate)),
      category: ACTION_CATEGORY.PBIS,
      usedIn: ['student', 'teacher', 'admin'],
    },
    {
      key: ACTION_KEY.ASSIGN_CONSEQUENCE,
      id: 'tools-dashboard-click-assign-consequence',
      label: ActionMesssages.CONSEQUENCE_BTN,
      icon: 'mg-behavior-menu-o',
      iconColor: getAppColor('behavior-praise'),
      onPress: () => {
        this._teacherToolsService.openPbisTrackingForm(PBIS_CHECKIN_TYPES.CONSEQUENCE);
      },
      hasPermission$: combineLatest([
        this._mingaSettings.isPbisModuleEnabled(),
        this._mingaSettings.getSettingValueObs(mingaSettingTypes.BM_CONSEQUENCE_ENABLE),
        this._permissionService.observePermission(MingaPermission.CONSEQUENCE_CREATE),
        this._permissionService.observePermission(MingaPermission.PBIS_TYPE_MANAGE),
      ]).pipe(
        map(
          ([settingEnabled, consequencesEnabled, canCreate, canManage]) =>
            settingEnabled && consequencesEnabled && (canCreate || canManage),
        ),
      ),
      category: ACTION_CATEGORY.PBIS,
      usedIn: ['student', 'teacher', 'admin'],
    },
    {
      key: ACTION_KEY.REDEEM_POINTS,
      id: 'tools-dashboard-click-redeem-points',
      label: ActionMesssages.REDEEM_POINTS_BTN,
      icon: 'mg-points-menu-o',
      iconColor: getAppColor('warning-300'),
      onPress: () => {
        this._redeemPointsModalService.openPeopleSelector();
      },
      hasPermission$: combineLatest([
        this._mingaSettings.getSettingValueObs(mingaSettingTypes.PM_REWARDS_STORE),
        this._permissionService.observePermission(MingaPermission.MANAGE_REWARDS),
        this._mingaSettings.isPbisModuleEnabled(),
      ]).pipe(
        map(([rewardStoreEnabled, manageRewards, pbisEnabled]) => rewardStoreEnabled && manageRewards && pbisEnabled),
      ),
      category: ACTION_CATEGORY.POINTS,
      usedIn: ['student', 'teacher', 'admin'],
    },
    {
      key: ACTION_KEY.MANAGE_POINTS,
      id: 'tools-dashboard-click-manage-points',
      label: ActionMesssages.MANAGE_POINTS_BTN,
      icon: 'mg-manage-points-o',
      iconColor: getAppColor('warning-300'),
      onPress: () => {
        this._teacherToolsService.openManagePointsForm();
      },
      hasPermission$: combineLatest([
        this._permissionService.observePermission(MingaPermission.POINTS_ALLOCATE_ALLOWED),
        this._permissionService.observePermission(MingaPermission.POINTS_MANAGE),
        this._mingaSettings.isPbisModuleEnabled(),
      ]).pipe(map(([canAllocate, pointsManage, pbisEnabled]) => (canAllocate || pointsManage) && pbisEnabled)),
      category: ACTION_CATEGORY.POINTS,
      usedIn: ['student', 'teacher', 'admin'],
    },

    {
      key: ACTION_KEY.KIOSK_MODE,
      id: 'tools-dashboard-click-kiosk',
      label: ActionMesssages.KIOSK_MODE_BTN,
      icon: 'mg-kiosk',
      iconColor: getAppColor('surface'),
      onPress: () => {
        this._teacherToolsService.showKioskModal();
      },
      hasPermission$: this._kioskPermissions.isKioskEnabled(),
      category: ACTION_CATEGORY.KIOSK,
      usedIn: ['teacher', 'admin'],
    },
  ];

  // Observables

  private _favoriteActionsSubject = new BehaviorSubject<ACTION_KEY[]>(ACTION_KEY_DEFAULTS);
  public favoriteActions$ = this._favoriteActionsSubject
    .asObservable()
    .pipe(map(keys => this.actions.filter(action => keys.includes(action.key))));

  // Component Constructor

  constructor(
    private _mingaSettings: MingaSettingsService,
    private _permissionService: PermissionsService,
    private _flexPermissionService: FlexTimePermissionsService,
    private _redeemPointsModalService: RedeemPointsModalService,
    private _teacherToolsService: TeacherToolsService,
    private _studentToolsCheckinService: StudentToolsCheckinService,
    private _kioskPermissions: KioskPermissionsService,
    private _localStorage: UserStorage,
  ) {
    this._initFavorites();
  }

  // Public Methods

  public toggleFavorite(option: ToolAction): void {
    const current = new Set(this._favoriteActionsSubject.value);

    current.has(option.key) ? current.delete(option.key) : current.add(option.key);

    const updated = Array.from(current);
    this._localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(updated));
    this._favoriteActionsSubject.next(updated);
  }

  // Private Methods

  private async _initFavorites() {
    const favorites = await this._loadFavoritesFromStorage();
    this._favoriteActionsSubject.next(favorites);
  }

  private async _loadFavoritesFromStorage(): Promise<ACTION_KEY[]> {
    try {
      const items = await this._localStorage.getItem<ACTION_KEY>(this.FAVORITES_KEY);
      return items ? JSON.parse(items) : ACTION_KEY_DEFAULTS;
    } catch {
      return ACTION_KEY_DEFAULTS;
    }
  }
}
